import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Link,
  Rating,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { apiCall } from '../../utils/fetchAPI';
import { useAuth } from '../../context/AuthContext';
import ModerateDialog from '../community/ModerateDialog';
import { authorInitials } from '../../constants/community';
import { Loading } from '../layout/Skeletons';
import { translateApiMessage } from '../../utils/apiMessages';
import ModeratorBadge from '../community/ModeratorBadge';

/**
 * The score, shown next to the provider's name.
 *
 * A listing with no visible review shows nothing rather than zero stars: no
 * reviews is not a bad rating.
 */
export function RatingSummary({ summary }) {
  if (!summary || 0 === summary.count) {
    return <Typography variant="body2">Aucun avis pour l'instant</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Rating value={summary.average} precision={0.1} readOnly size="small" />
      <Typography variant="body2">
        {summary.average.toFixed(1).replace('.', ',')} · {summary.count} avis
      </Typography>
    </Box>
  );
}

function ReviewForm({ providerSlug, onSubmitted }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setDone('');

    if (0 === rating) {
      setError('Choisissez une note.');
      return;
    }

    setSending(true);

    try {
      const response = await apiCall(`/providers/${providerSlug}/reviews`, {
        method: 'POST',
        body: JSON.stringify({ rating, comment: comment.trim() || null }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(translateApiMessage(data.error) || 'Votre avis n’a pas pu être envoyé.');
        return;
      }

      setDone(data.message || 'Merci ! Votre avis est publié.');
      setComment('');
      setRating(0);
      onSubmitted?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <Alert severity="success" sx={{ mt: 3 }}>
        {done}
      </Alert>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ mt: 3, p: { xs: 2.5, md: 3 }, borderRadius: '16px', bgcolor: 'background.default' }}
    >
      <Typography variant="h4" component="h4" sx={{ mb: 0.5 }}>
        Vous avez fait appel à eux&nbsp;?
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Racontez-le à vos voisins.
      </Typography>

      <Typography component="label" htmlFor="review-comment" variant="subtitle2" sx={{ display: 'block', mb: 1 }}>
        Votre note
      </Typography>

      {/* Large on purpose: a five-target row of 24px stars is the hardest
          thing to hit on the page. */}
      <Rating
        name="rating"
        value={rating}
        onChange={(event, value) => setRating(value || 0)}
        disabled={sending}
        size="large"
        sx={{ mb: 2.5, fontSize: '2.25rem' }}
      />

      <TextField
        label="Commentaire (facultatif)"
        id="review-comment"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder="Ce qui s'est bien passé, ce qui pourrait être mieux."
        multiline
        minRows={3}
        fullWidth
        disabled={sending}
        inputProps={{ maxLength: 2000 }}
      />

      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        size="large"
        startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
        disabled={sending}
        sx={{ mt: 2.5 }}
      >
        Envoyer mon avis
      </Button>
    </Box>
  );
}

/**
 * Reviews of one provider, and the form to add one.
 *
 * `isOwnListing` comes from the listing itself: a professional cannot review
 * their own, the API refuses it, so the form is not offered either. The page
 * used to work it out by comparing handles, which meant publishing the
 * owner's handle on every listing.
 */
function ProviderReviews({ providerSlug, citySlug, isOwnListing = false, summary: initialSummary }) {
  const { isAuthenticated, getDisplayName } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(initialSummary);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiCall(`/providers/${providerSlug}/reviews`, { method: 'GET' });

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      setReviews(data.data || []);
      setSummary(data.meta || null);
    } finally {
      setLoading(false);
    }
  }, [providerSlug]);

  useEffect(() => {
    load();
  }, [load]);

  // Reviews carry the author's display name, which is what getDisplayName()
  // builds for the signed-in resident.
  const alreadyReviewed = isAuthenticated
    && reviews.some((review) => review.author === getDisplayName());

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Typography variant="h3" component="h2">
          Les avis des voisins
        </Typography>
        <RatingSummary summary={summary} />
      </Box>

      {loading ? (
        <Loading label="Chargement des avis">
          {Array.from({ length: 2 }, (unused, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                gap: 1.75,
                py: 2.5,
                borderTop: '1px solid',
                borderColor: 'surface.rule',
                '&:first-of-type': { borderTop: 0, pt: 0 },
              }}
            >
              <Skeleton variant="circular" width={42} height={42} sx={{ flexShrink: 0 }} />
              <Box sx={{ flexGrow: 1 }}>
                <Skeleton variant="text" width={180} height={24} />
                <Skeleton variant="text" height={20} />
                <Skeleton variant="text" width="70%" height={20} />
              </Box>
            </Box>
          ))}
        </Loading>
      ) : (
        <Stack spacing={0}>
          {0 === reviews.length && (
            <Typography variant="body1">
              Personne n'a encore laissé d'avis. Soyez le premier.
            </Typography>
          )}

          {reviews.map((review) => (
            <Box
              key={review.uuid}
              sx={{
                display: 'flex',
                gap: 1.75,
                py: 2.5,
                borderTop: '1px solid',
                borderColor: 'surface.rule',
                '&:first-of-type': { borderTop: 0, pt: 0 },
              }}
            >
              <Avatar
                aria-hidden="true"
                sx={{
                  width: 42,
                  height: 42,
                  flexShrink: 0,
                  fontSize: '0.96875rem',
                  bgcolor: 'success.light',
                  color: 'secondary.dark',
                }}
              >
                {authorInitials(review.author)}
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mb: 0.5 }}>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {review.author}
                    <ModeratorBadge show={review.author_is_moderator} />
                  </Typography>
                  <Rating value={review.rating} readOnly size="small" />
                </Box>
                {review.comment && (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {review.comment}
                  </Typography>
                )}
                <ModerateDialog
                  targetType="REVIEW"
                  targetUuid={review.uuid}
                  citySlug={citySlug}
                  isOwn={isAuthenticated && review.author === getDisplayName()}
                  onModerated={load}
                />
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      {isAuthenticated && !isOwnListing && (
        <>
          {alreadyReviewed && (
            <Alert severity="info" sx={{ mt: 3 }}>
              Vous avez déjà donné votre avis. En envoyer un nouveau remplacera le précédent.
            </Alert>
          )}
          <ReviewForm providerSlug={providerSlug} onSubmitted={load} />
        </>
      )}

      {isAuthenticated && isOwnListing && (
        <Alert severity="info" sx={{ mt: 3 }}>
          C'est votre fiche : vous ne pouvez pas vous noter vous-même.
        </Alert>
      )}

      {!isAuthenticated && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Link component={RouterLink} to="/login">
            Connectez-vous
          </Link>{' '}
          pour laisser un avis.
        </Alert>
      )}
    </Box>
  );
}

export default ProviderReviews;
